import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from './ProductCard';
import apiService from '../services/nextlevelgames-api';
import pokemonLogo from '../assets/images/pokemon_logo.png';
import mtgLogo from '../assets/images/mtg_logo.png';
import yugiohLogo from '../assets/images/yugioh_logo.png';
import riftboundLogo from '../assets/images/riftbound_logo.png';

const ITEMS_PER_GAME = 20;

// Graded singles span several Square categories, so they're fetched by id
// (same ids as the GradedSingles page); the rest resolve by category name.
const GAMES = [
  { key: 'magic', label: 'Magic', logo: mtgLogo, categories: ['Magic Singles'], path: '/shop/magic' },
  { key: 'pokemon', label: 'Pokémon', logo: pokemonLogo, categories: ['Pokemon Singles'], path: '/shop/pokemon' },
  { key: 'yugioh', label: 'Yu-Gi-Oh!', logo: yugiohLogo, categories: ['Yugioh Singles'], path: '/shop/yugioh' },
  { key: 'onepiece', label: 'One Piece', emoji: '🏴‍☠️', categories: ['One Piece Singles'], path: '/shop/onepiece' },
  { key: 'pokemonjapan', label: 'Pokémon Japan', logo: pokemonLogo, categories: ['Pokemon Japan Singles'], path: '/shop/pokemonjapan' },
  { key: 'riftbound', label: 'Riftbound', logo: riftboundLogo, categories: ['Riftbound Singles'], path: '/shop/riftbound' },
  {
    key: 'graded',
    label: 'Graded',
    emoji: '🎴',
    categoryIds: [
      'VQW3LFT65KQUYDWQXGT5JJRK',
      'AWKYMZVXCWQ4SBTALWVVEW6Z',
      '2ENLBQLKJGNYBQQID755EEZL',
      'ZXDTH7YFYFE445URSQ2L2FHV',
    ],
    path: '/shop/gradedsingles',
  },
];

const toProduct = (item) => ({
  name: item.name,
  description: item.description,
  // Singles descriptions are "name\nset\nrarity\n..." lines
  set: (item.description || '').split('\n')[1] || '',
  price: item.price_money ? item.price_money.amount / 100 : 0,
  imageUrl: item.image_urls?.[0] || null,
  categories: item.categories || [],
  category: item.categories?.[0],
  updatedAt: item.updated_at || null,
  variation_id: item.variation_id,
  variation_name: item.variation_name,
  location_id: item.location_id,
  present_at_location_ids: item.present_at_location_ids || [],
  quantities_by_location: item.quantities_by_location || {},
  online_available: item.online_available === true,
});

const SinglesShowcase = () => {
  const [activeKey, setActiveKey] = useState(GAMES[0].key);
  const [itemsByGame, setItemsByGame] = useState({});
  const [errorKey, setErrorKey] = useState(null);
  const scrollerRef = useRef(null);

  const game = GAMES.find((g) => g.key === activeKey);

  // Lazy-load each game's row the first time its tab is opened, then cache it.
  useEffect(() => {
    if (itemsByGame[activeKey]) return undefined;
    let cancelled = false;
    setErrorKey(null);
    apiService
      .getCatalogImages(
        ITEMS_PER_GAME, 0, game.categories || [], 'newest', [], [], [], game.categoryIds || [], false, false
      )
      .then((data) => {
        if (!cancelled) {
          setItemsByGame((prev) => ({ ...prev, [activeKey]: (data.items || []).map(toProduct) }));
        }
      })
      .catch(() => {
        if (!cancelled) setErrorKey(activeKey);
      });
    return () => {
      cancelled = true;
    };
  }, [activeKey, game, itemsByGame]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTo({ left: 0 });
  }, [activeKey]);

  const scrollByPage = useCallback((dir) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
  }, []);

  const items = itemsByGame[activeKey];

  return (
    <section className="py-8 bg-gradient-to-b from-primary-100 via-primary-50 to-white">
      <div className="container">
        <div className="text-center mb-4">
          <h2 className="heading-block text-4xl">
            Shop Our <span className="text-gradient">Singles</span>
          </h2>
        </div>

        {/* Game picker */}
        <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-start sm:gap-3">
        <div className="flex gap-2 overflow-x-auto pb-2 sm:flex-1 sm:min-w-0 sm:[&>*:first-child]:ml-auto sm:[&>*:last-child]:mr-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist">
          {GAMES.map((g) => (
            <button
              key={g.key}
              role="tab"
              aria-selected={g.key === activeKey}
              onClick={() => setActiveKey(g.key)}
              className={`flex items-center gap-2 flex-shrink-0 px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                g.key === activeKey
                  ? 'bg-primary-600 border-primary-600 text-white shadow'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              {g.logo ? (
                <img src={g.logo} alt="" className="h-5 w-auto max-w-[2.5rem] object-contain" />
              ) : (
                g.emoji && <span aria-hidden="true">{g.emoji}</span>
              )}
              {g.label}
            </button>
          ))}
        </div>
          <Link to={game.path} className="btn-viewall self-end sm:self-start sm:mt-1">
            View all
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Product row */}
        <div className="relative group/scroller">
          <button
            onClick={() => scrollByPage(-1)}
            className="hidden md:flex absolute -left-6 top-1/3 z-10 p-3.5 rounded-full bg-gray-500/90 hover:bg-gray-600 shadow-lg opacity-0 group-hover/scroller:opacity-100 focus-visible:opacity-100 transition-opacity duration-200"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-7 h-7 text-white" />
          </button>
          <button
            onClick={() => scrollByPage(1)}
            className="hidden md:flex absolute -right-6 top-1/3 z-10 p-3.5 rounded-full bg-gray-500/90 hover:bg-gray-600 shadow-lg opacity-0 group-hover/scroller:opacity-100 focus-visible:opacity-100 transition-opacity duration-200"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-7 h-7 text-white" />
          </button>

          <div
            ref={scrollerRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 px-1 min-h-[300px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {!items && errorKey !== activeKey && (
              <div className="flex-1 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
              </div>
            )}
            {errorKey === activeKey && !items && (
              <p className="flex-1 text-center text-gray-500 self-center">
                Couldn't load cards right now.{' '}
                <Link to={game.path} className="text-primary-600 font-medium">Browse the shop page instead</Link>
              </p>
            )}
            {items && items.length === 0 && (
              <p className="flex-1 text-center text-gray-500 self-center">
                Nothing in stock here right now — check back soon.
              </p>
            )}
            {items &&
              items.map((item) => (
                <div key={item.variation_id || item.name} className="snap-start flex-shrink-0 w-40 sm:w-48">
                  <ProductCard product={item} imageUrl={item.imageUrl} showCondition viewMode="showcase" ratio="card" />
                </div>
              ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default SinglesShowcase;
